// src/mobile_pages/design/Detail.jsx
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  getDesignFolderTree,
  createDesignFolder,
  uploadDesignDoc,
  getDesignDocDetail,
  deleteDesignFolder,
} from "../../api/design";
import MobileDesignDocView from "../../components/design/MobileDesignDocView";
import { Folder, FolderOpen, FileText, ChevronRight, ChevronDown, Plus, Trash2, Upload, X } from "lucide-react";

export default function DesignLibrary() {
  const { id: projectId } = useParams();

  const [treeData, setTreeData] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docDetail, setDocDetail] = useState(null);
  const [mode, setMode] = useState("view"); // view | upload | new-folder

  // 업로드 상태
  const [file, setFile] = useState(null);
  const [discipline, setDiscipline] = useState("ARCH");
  const [category, setCategory] = useState("도면");
  const [version, setVersion] = useState("v1.0");
  const [comment, setComment] = useState("");

  // 새 폴더 상태
  const [newFolderName, setNewFolderName] = useState("");

  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // ----------------------
  // 헬퍼 함수
  // ----------------------
  const findNodeByKey = (nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const hasDocDescendant = (node) => {
    if (!node || !node.children) return false;
    for (const child of node.children) {
      if (typeof child.key === "string" && child.key.startsWith("doc-")) {
        return true;
      }
      if (!child.isLeaf && hasDocDescendant(child)) {
        return true;
      }
    }
    return false;
  };

  const hasFilesInSelectedFolder = useMemo(() => {
    if (!selectedFolder) return false;
    const node = findNodeByKey(treeData, selectedFolder);
    return hasDocDescendant(node);
  }, [selectedFolder, treeData]);

  const collectAllKeys = (nodes) => {
    const keys = new Set();
    const traverse = (list) => {
      list.forEach((node) => {
        keys.add(node.key);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(nodes);
    return keys;
  };

  // ----------------------
  // 트리 불러오기
  // ----------------------
  const loadTree = async () => {
    setLoading(true);
    try {
      const res = await getDesignFolderTree(projectId);

      const formatNode = (node) => ({
        key: node.isLeaf ? `doc-${node.id}` : `folder-${node.id}`,
        title: node.name,
        isLeaf: node.isLeaf || false,
        children: node.children ? node.children.map(formatNode) : [],
      });

      const formatted = res.data.map(formatNode);
      setTreeData(formatted);
      setExpandedKeys(collectAllKeys(formatted));
    } catch (err) {
      console.error("폴더 트리 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTree();
  }, [projectId]);

  // ----------------------
  // 노드 선택
  // ----------------------
  const handleNodeClick = async (node) => {
    if (node.key.startsWith("doc-")) {
      const docId = node.key.replace("doc-", "");
      setSelectedDoc(node.title);
      setSelectedFolder(null);
      setMode("view");

      try {
        const res = await getDesignDocDetail(docId);
        setDocDetail(res.data);
      } catch (err) {
        console.error("문서 상세 불러오기 실패:", err);
      }
    } else if (node.key.startsWith("folder-")) {
      setSelectedFolder(node.key);
      setSelectedDoc(null);
      setDocDetail(null);

      // 토글 확장
      setExpandedKeys((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(node.key)) {
          newSet.delete(node.key);
        } else {
          newSet.add(node.key);
        }
        return newSet;
      });
    }
  };

  // ----------------------
  // 업로드
  // ----------------------
  const handleUpload = async () => {
    if (!file || !selectedFolder) {
      alert("파일과 폴더를 선택하세요!");
      return;
    }
    const formData = new FormData();
    formData.append("project", projectId);
    formData.append("folder", selectedFolder.replace("folder-", ""));
    formData.append("name", file.name);
    formData.append("discipline", discipline);
    formData.append("category", category);
    formData.append("version", version);
    formData.append("file", file);
    formData.append("comment", comment);

    try {
      await uploadDesignDoc(formData);
      alert("업로드 성공!");
      setMode("view");
      setSelectedDoc(file.name);
      setFile(null);
      loadTree();
    } catch (err) {
      console.error("업로드 실패:", err.response?.data || err);
      alert("업로드 실패");
    }
  };

  // ----------------------
  // 새 폴더 생성
  // ----------------------
  const handleCreateFolder = async () => {
    if (!newFolderName) {
      alert("폴더명을 입력하세요!");
      return;
    }
    try {
      await createDesignFolder({
        project: projectId,
        name: newFolderName,
        parent: selectedFolder ? selectedFolder.replace("folder-", "") : null,
      });
      alert("폴더 생성 완료!");
      setMode("view");
      setNewFolderName("");
      loadTree();
    } catch (err) {
      console.error("폴더 생성 실패:", err);
      alert("폴더 생성 실패");
    }
  };

  // ----------------------
  // 폴더 삭제
  // ----------------------
  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;

    if (hasFilesInSelectedFolder) {
      alert(
        "폴더 내 파일이 있는 경우 삭제할 수 없습니다.\n먼저 해당 폴더(및 하위 폴더)의 파일을 모두 삭제하세요."
      );
      return;
    }

    const id = Number(selectedFolder.replace("folder-", ""));
    if (!Number.isFinite(id)) return;

    if (!window.confirm("정말로 이 폴더를 삭제할까요?")) {
      return;
    }

    try {
      await deleteDesignFolder(id);
      setSelectedFolder(null);
      setSelectedDoc(null);
      setDocDetail(null);
      await loadTree();
      alert("폴더 삭제 완료!");
    } catch (err) {
      console.error("폴더 삭제 실패:", err);
      alert("폴더 삭제 실패");
    }
  };

  // ----------------------
  // 트리 노드 렌더링 (모바일 터치 친화적)
  // ----------------------
  const renderTreeNode = (node, level = 0) => {
    const isFolder = node.key.startsWith("folder-");
    const isExpanded = expandedKeys.has(node.key);
    const isSelected = selectedFolder === node.key || selectedDoc === node.title;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.key}>
        <button
          onClick={() => handleNodeClick(node)}
          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-all rounded-lg mb-1
            ${isSelected
              ? "bg-blue-100 border border-blue-300"
              : "bg-gray-50 hover:bg-gray-100 border border-transparent"
            }`}
          style={{ marginLeft: level * 16 }}
        >
          {/* 확장 아이콘 (폴더만) */}
          {isFolder && hasChildren && (
            <span className="flex-shrink-0 text-gray-400">
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}

          {/* 폴더/파일 아이콘 */}
          <span className={`flex-shrink-0 ${isFolder ? "text-yellow-500" : "text-blue-500"}`}>
            {isFolder ? (
              isExpanded ? <FolderOpen size={18} /> : <Folder size={18} />
            ) : (
              <FileText size={18} />
            )}
          </span>

          {/* 제목 */}
          <span className={`flex-1 text-sm truncate ${isSelected ? "font-medium text-blue-800" : "text-gray-700"}`}>
            {node.title}
          </span>
        </button>

        {/* 하위 노드 */}
        {isFolder && isExpanded && hasChildren && (
          <div>
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing pb-20">
        {/* 헤더 */}
        <div className="section-card p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-bold text-gray-900">도서 라이브러리</h2>
              <p className="text-xs text-gray-500 mt-0.5">설계 도서 관리</p>
            </div>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="section-card p-3">
          <div className="flex gap-2">
            <button
              onClick={() => setMode("new-folder")}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <Plus size={16} />
              새 폴더
            </button>
            <button
              onClick={() => setMode("upload")}
              disabled={!selectedFolder}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={16} />
              업로드
            </button>
            <button
              onClick={handleDeleteFolder}
              disabled={!selectedFolder || hasFilesInSelectedFolder}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={16} />
            </button>
          </div>
          {selectedFolder && (
            <p className="text-xs text-blue-600 mt-2">
              선택된 폴더: {findNodeByKey(treeData, selectedFolder)?.title || "-"}
            </p>
          )}
        </div>

        {/* 트리 영역 */}
        <div className="section-card p-3">
          <div className="text-xs font-medium text-gray-500 mb-2">폴더 구조</div>
          <div className="max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="space-y-2">
                <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                <div className="h-10 bg-gray-100 rounded-lg animate-pulse ml-4" />
                <div className="h-10 bg-gray-100 rounded-lg animate-pulse ml-4" />
              </div>
            ) : treeData.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                등록된 폴더가 없습니다.
              </div>
            ) : (
              treeData.map((node) => renderTreeNode(node, 0))
            )}
          </div>
        </div>

        {/* 문서 상세 */}
        {mode === "view" && docDetail && (
          <div className="section-card p-4">
            <MobileDesignDocView docDetail={docDetail} />
          </div>
        )}

        {/* 업로드 모달 */}
        {mode === "upload" && (
          <div className="section-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">새 문서 업로드</h3>
              <button onClick={() => setMode("view")} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">파일 선택</label>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-600 file:mr-2 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">분야</label>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                >
                  <option value="ARCH">건축</option>
                  <option value="STRU">구조</option>
                  <option value="ELEC">전기</option>
                  <option value="MECH">설비</option>
                  <option value="CIVIL">토목</option>
                  <option value="ETC">기타</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">문서 유형</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="도면"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">버전</label>
                  <input
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="v1.0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">코멘트</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="업로드 코멘트"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                  rows={2}
                />
              </div>

              <button
                onClick={handleUpload}
                disabled={!file}
                className="w-full btn-save py-3 disabled:opacity-50"
              >
                업로드
              </button>
            </div>
          </div>
        )}

        {/* 새 폴더 모달 */}
        {mode === "new-folder" && (
          <div className="section-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">새 폴더 생성</h3>
              <button onClick={() => setMode("view")} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            {selectedFolder && (
              <p className="text-xs text-blue-600">
                상위 폴더: {findNodeByKey(treeData, selectedFolder)?.title || "루트"}
              </p>
            )}

            <div>
              <label className="block text-xs text-gray-600 mb-1">폴더명</label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="폴더명을 입력하세요"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
              />
            </div>

            <button
              onClick={handleCreateFolder}
              disabled={!newFolderName}
              className="w-full btn-create py-3 disabled:opacity-50"
            >
              폴더 생성
            </button>
          </div>
        )}

        {/* 빈 상태 */}
        {mode === "view" && !docDetail && !selectedDoc && (
          <div className="section-card p-6 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-4">
              문서를 선택하거나 폴더를 선택 후 업로드하세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
